// footer.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faXTwitter, faTiktok, faInstagram, faLinkedin, faYoutube } from '@fortawesome/free-brands-svg-icons';

const Footer = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentYear = new Date().getFullYear();

  // Fetch contact info from Statamic API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch('/api/contact-info');
        if (!response.ok) {
          throw new Error('Failed to fetch contact info');
        }
        const data = await response.json();
        setContactInfo(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching contact info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const quickLinks = [
    { name: 'Home', path: '/homepage-premium-healthcare-digital-experience' },
    { name: 'About', path: '/about-our-story-medical-philosophy' },
    { name: 'Services', path: '/services-comprehensive-eye-care-universe' },
    { name: 'Our Team', path: '/our-team-expert-network-personal-connections' },
    { name: 'Locations', path: '/locations-community-health-hub-network' },
    { name: 'Book Appointment', path: '/book-appointment-guided-care-journey' },
    { name: 'Contact', path: '/contact-multi-channel-communication-hub' },
  ];

  const services = [
    'Comprehensive Eye Exams',
    'LASIK Surgery',
    'Cataract Surgery',
    'Glaucoma Treatment',
    'Retinal Disorders',
    'Pediatric Eye Care'
  ];

  // Create socialLinks from contactInfo
  const socialLinks = contactInfo?.social_media_links?.map(link => {
    // Map platform names to FontAwesome icons
    const iconMap = {
      'Facebook': faFacebook,
      'X (Twitter)': faXTwitter,
      'Instagram': faInstagram,
      'LinkedIn': faLinkedin,
      'YouTube': faYoutube,
      'TikTok': faTiktok
    };

    return {
      name: link.platform,
      icon: iconMap[link.platform],
      url: link.url
    };
  }) || [];

  // Update emergency handlers to use contactInfo data
  const handleEmergencyCall = () => {
    if (contactInfo) {
      window.open(`tel:${contactInfo.phone_number}`, '_self');
    }
  };

  const handleWhatsAppEmergency = () => {
    if (contactInfo) {
      window.open(`https://wa.me/${contactInfo.whatsapp_number.replace(/\D/g, '')}?text=Emergency%20Eye%20Care%20Needed`, '_blank');
    }
  };

  return (
      <footer className="bg-slate-900 text-white">
          {/* Emergency Banner */}
          <div className="bg-[#eb2227] py-4">
              <div className="max-w-7xl mx-auto px-4 lg:px-6">
                  <div className="flex flex-col md:flex-row items-center justify-between">
                      <div className="flex items-center space-x-3 mb-4 md:mb-0">
                          {/* <Icon name="AlertCircle" size={24} className="animate-pulse" /> --- IGNORE ---*/}
                          <div>
                              {/*<h3 className="font-headline font-bold text-lg">Emergency Eye Care (During Opening Hours)</h3>
                <p className="font-body text-sm opacity-90">Fast response for urgent eye conditions when you need us</p> */}
                          </div>
                      </div>
                      <div className="flex space-x-3">
                          {/*<Button
                variant="ghost"
                onClick={handleEmergencyCall}
                iconName="Phone"
                iconPosition="left"
                className="text-white hover:bg-white/20"
              >
                Call Emergency
              </Button>
              <Button
                variant="ghost"
                onClick={handleWhatsAppEmergency}
                iconName="MessageCircle"
                iconPosition="left"
                className="text-white hover:bg-white/20"
              >
                WhatsApp
              </Button> -- IGNORE --*/}
                      </div>
                  </div>
              </div>
          </div>

          {/* Main Footer Content */}
          <div className="py-16">
              <div className="max-w-7xl mx-auto px-4 lg:px-6">
                  <div className="grid lg:grid-cols-5 gap-8">
                      {/* Brand Section */}
                      <div className="lg:col-span-2 space-y-6">
                          <Link
                              to="/homepage-premium-healthcare-digital-experience"
                              className="flex items-center space-x-3"
                          >
                              <img
                                  src="/storage/logo_icon/eye-u-logoo.png"
                                  alt="Eye&U Logo"
                                  className="w-20 h-20 object-contain rounded-lg"
                              />
                              <div className="flex flex-col">
                                  <span className="font-headline font-bold text-2xl">
                                      Eye&U Ophthalmics
                                  </span>
                                  <span className="font-body text-sm text-slate-400 -mt-1">
                                      For Your Eyes Only
                                  </span>
                              </div>
                          </Link>

                          <p className="font-body text-slate-300 leading-relaxed max-w-md">
                              To be at the forefront of eliminating preventable
                              blindness in Kenya by delivering world-class,
                              affordable, and compassionate eye care. Through
                              innovation, advanced technology, and highly
                              skilled specialists, we strive to restore vision,
                              improve lives, and create brighter futures for our
                              patients and communities.
                          </p>

                          {/* Certifications */}
                          <div className="space-y-3">
                              <h4 className="font-headline font-semibold text-lg">
                                  Certifications
                              </h4>
                              <div className="flex flex-wrap gap-4">
                                  <div className="flex items-center space-x-2">
                                      <Icon
                                          name="Shield"
                                          size={16}
                                          className="text-white"
                                      />
                                      <span className="font-body text-sm text-slate-300">
                                          Kenya Medical Board
                                      </span>
                                  </div>
                              </div>
                          </div>

                          {/* Social Links */}
                          <div className="space-y-3">
                              <h4 className="font-headline font-semibold text-lg">
                                  Follow Us
                              </h4>
                              <div className="flex space-x-3">
                                  {loading ? (
                                      <div>Loading social links...</div>
                                  ) : error ? (
                                      <div>Error loading social links</div>
                                  ) : (
                                      socialLinks.map((social) => (
                                          <a
                                              key={social.name}
                                              href={social.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-10 h-10 bg-slate-800 hover:bg-primary rounded-lg flex items-center justify-center transition-smooth"
                                          >
                                              <FontAwesomeIcon
                                                  icon={social.icon}
                                                  className="text-white text-lg"
                                              />
                                          </a>
                                      ))
                                  )}
                              </div>
                          </div>
                      </div>

                      {/* Quick Links */}
                      <div className="space-y-6">
                          <h4 className="font-headline font-semibold text-lg">
                              Quick Links
                          </h4>
                          <ul className="space-y-3">
                              {quickLinks.map((link) => (
                                  <li key={link.path}>
                                      <Link
                                          to={link.path}
                                          className="font-body text-slate-300 hover:text-white transition-smooth flex items-center space-x-2"
                                      >
                                          <Icon name="ChevronRight" size={14} />
                                          <span>{link.name}</span>
                                      </Link>
                                  </li>
                              ))}
                          </ul>
                      </div>

                      {/* Services */}
                      <div className="space-y-6">
                          <h4 className="font-headline font-semibold text-lg">
                              Our Services
                          </h4>
                          <ul className="space-y-3">
                              {services.map((service) => (
                                  <li key={service}>
                                      <Link
                                          to="/services-comprehensive-eye-care-universe"
                                          className="font-body text-slate-300 hover:text-white transition-smooth flex items-center space-x-2"
                                      >
                                          <Icon name="Eye" size={14} />
                                          <span>{service}</span>
                                      </Link>
                                  </li>
                              ))}
                          </ul>
                      </div>

                      {/* Contact & Locations */}
                      <div className="space-y-6">
                          <h4 className="font-headline font-semibold text-lg">
                              Our Locations
                          </h4>
                          <div className="space-y-4">
                              {loading ? (
                                  <div>Loading locations...</div>
                              ) : error ? (
                                  <div>Error loading locations</div>
                              ) : (
                                  contactInfo?.locations?.map(
                                      (location, index) => (
                                          <div
                                              key={index}
                                              className="space-y-2"
                                          >
                                              <h5 className="font-body font-medium text-white">
                                                  {location.location_name}
                                              </h5>
                                              <div className="flex items-start space-x-2">
                                                  <Icon
                                                      name="MapPin"
                                                      size={14}
                                                      className="text-slate-400 mt-1 flex-shrink-0"
                                                  />
                                                  <span className="font-body text-sm text-slate-300">
                                                      {location.address}
                                                  </span>
                                              </div>
                                              <div className="flex items-center space-x-2">
                                                  <Icon
                                                      name="Phone"
                                                      size={14}
                                                      className="text-slate-400 flex-shrink-0"
                                                  />
                                                  <a
                                                      href={`tel:${location.phone}`}
                                                      className="font-body text-sm text-slate-300 hover:text-white transition-smooth"
                                                  >
                                                      {location.phone}
                                                  </a>
                                              </div>
                                          </div>
                                      )
                                  )
                              )}
                          </div>

                          {/* Contact Info */}
                          <div className="space-y-3">
                              <h5 className="font-body font-medium text-white">
                                  General Contact
                              </h5>
                              <div className="space-y-2">
                                  {loading ? (
                                      <div>Loading contact info...</div>
                                  ) : error ? (
                                      <div>Error loading contact info</div>
                                  ) : (
                                      contactInfo && (
                                          <div className="flex items-center space-x-2">
                                              <Icon
                                                  name="Mail"
                                                  size={14}
                                                  className="text-slate-400"
                                              />
                                              <a
                                                  href={`mailto:${contactInfo.email_address}`}
                                                  className="font-body text-sm text-slate-300 hover:text-white transition-smooth"
                                              >
                                                  {contactInfo.email_address}
                                              </a>
                                          </div>
                                      )
                                  )}
                                  <div className="flex items-center space-x-2">
                                      <Icon
                                          name="Clock"
                                          size={14}
                                          className="text-slate-400"
                                      />
                                      <span className="font-body text-sm text-slate-300">
                                          Mon-Fri: 8:30AM-5:00PM
                                      </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                      <Icon
                                          name="Clock"
                                          size={14}
                                          className="text-slate-400"
                                      />
                                      <span className="font-body text-sm text-slate-300">
                                          Sat: 8:30AM-1:00PM
                                      </span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 py-6">
              <div className="max-w-7xl mx-auto px-4 lg:px-6">
                  <div className="flex flex-col md:flex-row items-center justify-between">
                      <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 mb-4 md:mb-0">
                          <p className="font-body text-sm text-slate-400">
                              © {currentYear} Eye&U Kenya. All rights reserved.
                          </p>
                          <div className="flex items-center space-x-4">
                              <Link
                                  to="#"
                                  className="font-body text-sm text-slate-400 hover:text-white transition-smooth"
                              >
                                  Privacy Policy
                              </Link>
                              <Link
                                  to="#"
                                  className="font-body text-sm text-slate-400 hover:text-white transition-smooth"
                              >
                                  Terms of Service
                              </Link>
                              <Link
                                  to="#"
                                  className="font-body text-sm text-slate-400 hover:text-white transition-smooth"
                              >
                                  Medical Disclaimer
                              </Link>
                          </div>
                      </div>

                      <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-2 text-center md:text-left">
                          <span className="font-body text-sm text-slate-400">
                              Built with{" "}
                              <Icon
                                  name="Heart"
                                  size={12}
                                  className="text-red-500 inline-block mx-1"
                              />{" "}
                              for quality eye care.
                          </span>
                          <span className="font-body text-sm text-slate-400">
                              Designed and developed by{" "}
                              <a
                                  href="https://www.linkedin.com/in/chrispusmwangi/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-accent transition-smooth font-medium"
                              >
                                  Chris Mwangi
                              </a>
                          </span>
                      </div>
                  </div>
              </div>
          </div>
      </footer>
  );
};

export default Footer;